import { Component, Inject, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { distinctUntilChanged, map, Subscription } from 'rxjs';

import { Logger, PreviewMode, UtilityService } from 'src/app/@shared';
import { Offer, OfferGroupDomain } from '../..';
import { ListViewStateService } from '../../services/list-view-state.service';

type SelectedRecord = OfferGroupDomain<Offer>;

const log = new Logger('SelectedRecordsDialogComponent');

@Component({
  selector: 'app-selected-records-dialog',
  templateUrl: './selected-records-dialog.component.html',
  styleUrls: ['./selected-records-dialog.component.scss'],
})
export class SelectedRecordsDialogComponent implements AfterViewInit, OnDestroy {
  PreviewMode = PreviewMode;

  displayedColumns: string[] = ['Image', 'Name', 'Headline', 'BodyCopy', 'Actions'];
  dataSource = new MatTableDataSource<SelectedRecord>([]);
  selectedCount = 0;
  filterValue = '';

  private subs = new Subscription();

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public utilityService: UtilityService,
    public listViewState: ListViewStateService,
    @Inject(MAT_DIALOG_DATA) public data: { eventId?: string },
  ) {
    this.dataSource.filterPredicate = (record, filter) => {
      const f = (filter ?? '').trim().toLowerCase();
      if (!f) return true;

      const name = String(record?.Detail?.Name ?? '').toLowerCase();
      const headline = String(record?.Detail?.Headline ?? '').toLowerCase();
      const bodyCopy = String(record?.Detail?.BodyCopy ?? '').toLowerCase();
      return name.includes(f) || headline.includes(f) || bodyCopy.includes(f);
    };

    this.dataSource.sortingDataAccessor = (record: any, column: string) => {
      switch (column) {
        case 'Name':
          return record?.Detail?.Name ?? '';
        case 'Headline':
          return record?.Detail?.Headline ?? '';
        case 'BodyCopy':
          return record?.Detail?.BodyCopy ?? '';
        default:
          return record?.[column] ?? '';
      }
    };

    this.subs.add(
      this.listViewState.listViewState$
        .pipe(
          map(s => (s.selectedRecords ?? []) as SelectedRecord[]),
          map(records => ({
            records,
            key: records.map(r => String(r?.DomainId ?? '')).sort().join('|'),
          })),
          distinctUntilChanged((a, b) => a.key === b.key),
        )
        .subscribe({
          next: ({ records }) => {
            this.dataSource.data = records;
            this.selectedCount = records.length;
            this.dataSource.filter = (this.filterValue ?? '').trim().toLowerCase();
          },
          error: (e) => log.error('Failed to update selected records table', e),
        })
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  applyFilter(value: string) {
    this.filterValue = value ?? '';
    this.dataSource.filter = this.filterValue.trim().toLowerCase();
  }

  remove(record: SelectedRecord) {
    if (!record?.DomainId) return;
    this.listViewState.toggleSelection(record, 'DomainId');
  }

  clearAll() {
    this.listViewState.clearSelection();
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png';
  }
}

